/* ==========================================================================
   hst-product-form.js — <product-form> shim for the Hestern host theme
   ==========================================================================

   WHY THIS FILE EXISTS
   The hst-* homepage sections render their add-to-cart control as

       <product-form>
         {% form 'product', product %} … <button name="add" class="btn-atc">
       {% endform %}</product-form>

   at snippets/hst-product-card.liquid, snippets/hst-premium-mcard.liquid,
   sections/hst-showcase.liquid and sections/hst-premium.liquid.

   In the donor theme `product-form` is defined in assets/product.js, which
   delegates the network call to `document.querySelector('cart-container').add()`
   in assets/cart.js. NONE of that exists here: this theme has no
   <cart-container>, no 'cart-count-badge'/'offcanvas-cart' sections and no
   Bootstrap offcanvas cart. Copying product.js across would throw on the first
   click, so this file re-implements only the submit handler and routes the
   result into THIS theme's own mini-cart API instead.

   WITHOUT this file the buttons are not broken — the inner <form> still POSTs
   natively to /cart/add and the shopper lands on the cart page. This upgrades
   that to an in-place AJAX add.

   HOST THEME CONTRACT (all verified in assets/theme.min.js / theme.js)
     theme.miniCart.updateElements()   theme.js:4789  — .js-cart-count,
                                       .js-cart-total, free-shipping bar
     theme.miniCart.generateCart()     theme.js:4810  — rebuilds
                                       .js-mini-cart-content line items
     theme.alert.new(t, msg, ms, kind) theme.js:6090  — growl toast
     theme.cartpage                    snippets/header-js.liquid:32
     window.routes.cart_add_url        snippets/header-js.liquid:21

   NOTE ON THE DRAWER: settings.cartdraw is true, but the rule that slides the
   panel back into view — `.cardraw.active .mini-cart-content{transform:none}` —
   is commented out at snippets/header-oncss.liquid:1264-1281. Adding .active
   therefore shows nothing today. We add it anyway (so the drawer works the day
   that block is un-commented) AND always raise the toast, so the shopper gets
   feedback either way.

   Every theme.* touch is guarded with typeof: assets/vendor.js and custom.js
   are encrypted eval payloads, so nothing here may assume jQuery or a
   particular init order. Loaded with `defer` at the end of <body>, after
   header-js, so theme.miniCart already exists by the time a click can happen.
   ========================================================================== */
(function () {
  'use strict';

  /* Never clobber a real implementation — if product.js is ever ported in,
     this whole file becomes a no-op. */
  if (!window.customElements || customElements.get('product-form')) return;

  var ADD_URL = (window.routes && window.routes.cart_add_url)
    ? window.routes.cart_add_url + '.js'
    : '/cart/add.js';

  function t() { return window.theme || null; }

  /* Title is a PARAMETER, never baked in. Baking in
     theme.strings.addToCartSuccess would head the failure growl with
     "Item(s) added to cart." while the cart count sits still — the page
     asserting the opposite of what happened. The theme's own handlers pass an
     empty title on failure (theme.js:1364, theme.js:4555); match that. */
  function toast(title, message, kind) {
    var th = t();
    if (!th || !th.alert || typeof th.alert.new !== 'function') return;
    try {
      th.alert.new(title || '', message || '', 3000, kind || 'notice');
    } catch (e) { /* growl not booted yet — silent, the badge still updated */ }
  }

  function successTitle() {
    var th = t();
    return (th && th.strings && th.strings.addToCartSuccess) || '';
  }

  /* Re-sync every piece of host cart chrome after a successful add. */
  function refreshHostCart() {
    var th = t();
    if (th && th.miniCart) {
      if (typeof th.miniCart.updateElements === 'function') {
        try { th.miniCart.updateElements(); } catch (e) {}
      }
      if (typeof th.miniCart.generateCart === 'function') {
        try { th.miniCart.generateCart(); } catch (e) {}
      }
    }

    var carts = document.querySelectorAll('.js-mini-cart');
    for (var i = 0; i < carts.length; i++) carts[i].classList.add('active');

    /* The cart template renders totals server-side; the host theme reloads
       there rather than patching the DOM (theme.js:4679). Match that. */
    if (th && th.cartpage) window.location.reload();
  }

  /* `class extends HTMLElement` — the same form assets/global.js already uses
     for QuantityInput, MenuDrawer, DeferredMedia et al., so it is proven to
     upgrade in every browser this theme supports. Wiring happens in
     connectedCallback, not the constructor: the element is parsed before its
     inner <form> exists, so querying in the constructor would find nothing. */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._hstBound) return;
      this._hstBound = true;
      this.form = this.querySelector('form');
      this.atcBtn = this.querySelector('.btn-atc') || this.querySelector('[name="add"]');
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setBusy(busy) {
      var b = this.atcBtn;
      if (!b) return;
      b.classList[busy ? 'add' : 'remove']('loading');
      b.disabled = busy;
      b.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    onSubmit(event) {
      if (!this.form) return;
      event.preventDefault();

      var self = this;
      self.setBusy(true);

      /* FormData carries id + quantity + form_type + utf8 straight from
         {% form 'product' %}. Do NOT append `sections`/`sections_url` — this
         theme ignores the Section Rendering API and generateCart() re-fetches
         /cart.js itself. */
      fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(self.form)
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          if (r.ok) {
            refreshHostCart();
            toast(successTitle(), '', 'notice');
          } else {
            /* 422 "You can only add N of that to your cart", or a variant that
               sold out between page load and this click. Title stays empty. */
            toast('', (r.data && (r.data.description || r.data.message)) || '', 'warning');
          }
          self.setBusy(false);
        })
        .catch(function () {
          /* Network died mid-request — fall back to the native POST so the
             shopper is never left with a dead button. No setBusy here: the
             page is navigating away. */
          self.form.submit();
        });
    }
  }

  customElements.define('product-form', ProductForm);
})();
